import { useCallback, useEffect, useRef, useState } from "react";

import { Game } from "../../pkg";
import * as classes from "./App.module.css";
import { Canvas } from "./Canvas";

const TICK_DURATION = 20; // ms

const numberFormat = Intl.NumberFormat("en-US");

function num(number: number): string {
    return numberFormat.format(number);
}

interface UpgradeStats {
    baseValue: number;
    baseCost: number;
    valueAddend: number;
    costMultiplier: number;
}

interface UseUpgradeResult {
    value: number;
    cost: number;
    count: number;
    buy: () => void;
    setValue: (value: number) => void;
}

function useUpgrade(game: Game, props: UpgradeStats): UseUpgradeResult {
    const [value, setValue] = useState(props.baseValue);
    const [cost, setCost] = useState(props.baseCost);
    const [count, setCount] = useState(0);

    return {
        value,
        cost,
        count,
        buy: useCallback(() => {
            if (game.consume_score(cost)) {
                setValue((value) => value + props.valueAddend);
                setCost((cost) => Math.round(cost * props.costMultiplier));
                setCount((count) => count + 1);
            }
        }, [game, cost]),
        setValue,
    };
}

interface AppProps {
    game: Game;
}

export function App(props: AppProps) {
    const { game } = props;

    const [score, setScore] = useState(0);

    useEffect(() => {
        let lastTickTime = Date.now();
        const intervalId = window.setInterval(() => {
            while (lastTickTime + TICK_DURATION < Date.now()) {
                game.tick();
                lastTickTime += TICK_DURATION;
            }

            setScore(game.score);
        }, TICK_DURATION);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [game]);

    const sandBlobSize = useUpgrade(game, {
        baseValue: 1,
        baseCost: 10,
        valueAddend: 1,
        costMultiplier: 1.5,
    });

    const sandwormLifetime = useUpgrade(game, {
        baseValue: 5,
        baseCost: 50,
        valueAddend: 5,
        costMultiplier: 1.5,
    });

    const sandSpawnRate = useUpgrade(game, {
        baseValue: 0,
        baseCost: 100,
        valueAddend: 10,
        costMultiplier: 1.2,
    });

    const sandwormSpawnRate = useUpgrade(game, {
        baseValue: 0,
        baseCost: 500,
        valueAddend: 1,
        costMultiplier: 1.5,
    });

    const sandwormBlobSize = useUpgrade(game, {
        baseValue: 1,
        baseCost: 2000,
        valueAddend: 1,
        costMultiplier: 2.5,
    });

    const [gameOver, setGameOver] = useState(false);

    const sandwormLifetimeValueRef = useRef(sandwormLifetime.value);
    sandwormLifetimeValueRef.current = sandwormLifetime.value;
    useEffect(() => {
        if (sandwormSpawnRate.value === 0) {
            return;
        }

        let lastSpawnTime = Date.now();
        const id = window.setInterval(() => {
            while (
                lastSpawnTime + 1000 / sandwormSpawnRate.value <
                Date.now()
            ) {
                game.replace_random("Sand", {
                    Sandworm: sandwormLifetimeValueRef.current,
                });
                lastSpawnTime += 1000 / sandwormSpawnRate.value;
            }
        }, TICK_DURATION);
        return () => {
            window.clearInterval(id);
        };
    }, [game, sandwormSpawnRate.value]);

    useEffect(() => {
        if (sandSpawnRate.value === 0) {
            return;
        }

        let lastSpawnTime = Date.now();
        const id = window.setInterval(() => {
            while (lastSpawnTime + 1000 / sandSpawnRate.value < Date.now()) {
                game.replace_random("Empty", "Sand");
                lastSpawnTime += 1000 / sandSpawnRate.value;
            }
        }, TICK_DURATION);
        return () => {
            window.clearInterval(id);
        };
    }, [game, sandSpawnRate.value]);

    return (
        <div className={classes.root}>
            <div className={classes.panel}>
                <div className={classes.description}>
                    Left Click to spawn Sand,
                    <br />
                    Right Click on the Sand to spawn Sandworm,
                    <br /> Sandworm eats Sand to gain Spice
                </div>
                <div className={classes.score}>Spice: {num(score)}</div>
                <div className={classes.upgrades}>
                    <button
                        className={classes.upgradeButton}
                        disabled={gameOver || sandBlobSize.cost > score}
                        onClick={() => sandBlobSize.buy()}
                    >
                        Increase Sand Blob Size
                        <span>
                            (current: {num(sandBlobSize.value ** 2)} / click)
                        </span>
                        <span>(cost: {num(sandBlobSize.cost)} Spice)</span>
                    </button>
                    {sandBlobSize.count > 0 && (
                        <button
                            className={classes.upgradeButton}
                            disabled={gameOver || sandwormLifetime.cost > score}
                            onClick={() => sandwormLifetime.buy()}
                        >
                            Increase Sandworm Lifetime
                            <span>
                                (current:{" "}
                                {num(
                                    (sandwormLifetime.value * TICK_DURATION) /
                                        1000,
                                )}{" "}
                                sec ≈ {num(sandwormLifetime.value)}{" "}
                                Spice/Sandworm)
                            </span>
                            <span>
                                (cost: {num(sandwormLifetime.cost)} Spice)
                            </span>
                        </button>
                    )}
                    {sandwormLifetime.count > 0 && (
                        <button
                            className={classes.upgradeButton}
                            disabled={gameOver || sandSpawnRate.cost > score}
                            onClick={() => sandSpawnRate.buy()}
                        >
                            Increase Sand Spawn Rate
                            <span>
                                (current: {num(sandSpawnRate.value)} / sec)
                            </span>
                            <span>(cost: {num(sandSpawnRate.cost)} Spice)</span>
                        </button>
                    )}
                    {sandSpawnRate.count > 0 && (
                        <button
                            className={classes.upgradeButton}
                            disabled={
                                gameOver || sandwormSpawnRate.cost > score
                            }
                            onClick={() => sandwormSpawnRate.buy()}
                        >
                            Increase Sandworm Spawn Rate
                            <span>
                                (current: {num(sandwormSpawnRate.value)} / sec)
                            </span>
                            <span>
                                (cost: {num(sandwormSpawnRate.cost)} Spice)
                            </span>
                        </button>
                    )}
                    {sandwormSpawnRate.count > 0 && (
                        <button
                            className={classes.upgradeButton}
                            disabled={gameOver || sandwormBlobSize.cost > score}
                            onClick={() => sandwormBlobSize.buy()}
                        >
                            Increase Sandworm Blob Size
                            <span>
                                (current: {num(sandwormBlobSize.value ** 2)} /
                                click)
                            </span>
                            <span>
                                (cost: {num(sandwormBlobSize.cost)} Spice)
                            </span>
                        </button>
                    )}
                    {sandwormBlobSize.count > 0 && (
                        <button
                            className={classes.finalUpgradeButton}
                            disabled={gameOver || 20000 > score}
                            onClick={() => {
                                if (game.consume_score(20000)) {
                                    sandSpawnRate.setValue(2000);
                                    sandwormSpawnRate.setValue(10);
                                    sandwormLifetime.setValue(200);
                                    setGameOver(true);
                                }
                            }}
                        >
                            Yellow Screen Of Success
                            <span>(wins the game)</span>
                            <span>(cost: {num(20000)} Spice)</span>
                        </button>
                    )}
                </div>
            </div>

            <div className={classes.canvas}>
                <Canvas
                    game={game}
                    sandBlobSize={sandBlobSize.value}
                    sandwormBlobSize={sandwormBlobSize.value}
                    sandwormLifetime={sandwormLifetime.value}
                />
                {gameOver && (
                    <div className={classes.canvasGameOverOverlay}>
                        YOU WIN!
                        <button onClick={() => document.location.reload()}>
                            Play again?
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
